import React, { useMemo } from 'react';
import Grid from '@material-ui/core/Grid';
import { useTranslation } from 'react-i18next';
import Card from '@material-ui/core/Card';
import styled from 'styled-components';
import { ButtonVariations } from '../SolvPatternLibrary/Button';
import { colors } from '../../constants/colors';
import DefaultCardComponents, { CardState } from './util/CardComponents';
import H22022BookingFlowCardComponents from './util/H22022CardComponents';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';

type Props = {
  cardImage?: string;
  children?: any;
  onClick?: (...args: any[]) => any;
  showPlaceholder?: boolean;
  disabled?: boolean;
  scanStarted?: boolean;
  scanCompleted?: boolean;
  handleFileChange: (...args: any[]) => any;
  parentControlAnimation?: boolean;
  animationClassName?: string;
  buttonStyle?: string;
  placeholderCopy?: string;
  placeholderImage?: string;
  isSRPorCDP?: boolean;
};

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InsuranceCard = (props: Props) => {
  const {
    cardImage,
    showPlaceholder,
    scanStarted,
    scanCompleted,
    onClick,
    children,
    disabled,
    handleFileChange,
    parentControlAnimation,
    animationClassName = '',
    buttonStyle = ButtonVariations.pinkPrimary,
    placeholderCopy,
    placeholderImage,
    isSRPorCDP,
  } = props;
  const flags = useLDFlags();
  const { kis511H22022BookingFlow } = flags;

  const { t } = useTranslation('addInsurance');

  let cardState: CardState;
  if (cardImage) {
    cardState = 'photo';
  } else if (showPlaceholder) {
    cardState = 'placeholder';
  } else {
    cardState = 'blank';
  }

  const {
    Input,
    CardPlaceholder,
    ScanOverlay,
    ScanHead,
    CardPicture,
    HintContainer,
    UploadButton,
    UploadButtonJigsaw,
    Card,
    CardHeader,
    CardBody,
    CardContainer,
  } = useMemo(() => {
    /* The H2 2022 Booking Flow uses a very similar form & page layout as the previous version, but provides
     * different elements for rendering the various components. This useMemo selects the appropate components
     * with which to render the form. */
    if (kis511H22022BookingFlow && isSRPorCDP) {
      return H22022BookingFlowCardComponents;
    }
    return DefaultCardComponents;
  }, [isSRPorCDP, kis511H22022BookingFlow]);

  const showScanAnimation = scanStarted && !scanCompleted;

  const CurrentUploadButton = useMemo(
    () =>
      buttonStyle === ButtonVariations.tertiary ? (
        <UploadButton variation={buttonStyle}>{t('insuranceCard.takeAPhoto')}</UploadButton>
      ) : (
        <ButtonContainer>
          <UploadButtonJigsaw
            variant={buttonStyle === ButtonVariations.pinkTertiary ? 'outlined' : 'contained'}
          >
            {t('insuranceCard.takeAPhoto')}
          </UploadButtonJigsaw>
        </ButtonContainer>
      ),
    [buttonStyle, UploadButton, UploadButtonJigsaw, t]
  );

  switch (cardState) {
    case 'placeholder':
      return (
        <Card
          elevation={0}
          onClick={onClick}
          sc={{ borderStyle: 'dashed', borderColor: colors.black }}
          state={cardState}
        >
          <Input accept={'image/*'} data-hj-suppress onChange={handleFileChange} type="file" />

          <CardContainer>
            <CardPlaceholder src={placeholderImage} />

            <HintContainer>
              <CardHeader>{children}</CardHeader>

              <CardBody>{placeholderCopy}</CardBody>
            </HintContainer>
          </CardContainer>

          {/* Avoid rendering this wrapping Grid of one item if we've provided a
          custom button component. */}
          {kis511H22022BookingFlow ? (
            CurrentUploadButton
          ) : (
            <Grid item xs={12}>
              {CurrentUploadButton}
            </Grid>
          )}
        </Card>
      );
    case 'photo':
      return (
        <Card data-hj-suppress sc={{ borderColor: 'none', disabled }} state={cardState}>
          {showScanAnimation && (
            <ScanHead
              className={animationClassName}
              sc={{ selfAnimate: !parentControlAnimation }}
            />
          )}
          <CardPicture data-hj-suppress sc={{ imgData: cardImage }}>
            {showScanAnimation && (
              <ScanOverlay
                className={animationClassName}
                sc={{ selfAnimate: !parentControlAnimation }}
              />
            )}
          </CardPicture>
        </Card>
      );
    default:
    case 'blank':
      return (
        <Card sc={{ borderStyle: 'solid', borderColor: colors.borderLine }} state={cardState} />
      );
  }
};

export default InsuranceCard;
